import { z } from 'zod';
import { ObjectBehavior, PartialObjectBehavior } from './object-behavior';
import { ApiKey, StableKey, Uuid } from './ids';
import { HexColor, ObjectColor, ObjectIcon } from './colors';
import {
  LocaleCode,
  LocalizedText,
  isLocalizedTextMap,
  localizedText,
  localizedTextOf,
  resolveText,
} from './localized-text';

// ---------------------------------------------------------------------------------------------
// Translatable labels (plan §8.10)
// ---------------------------------------------------------------------------------------------

/**
 * A metadata label as written: a plain string (every locale) or one value per locale
 * (`{ en: 'Deals', de: 'Verkaufschancen' }`), 1–80 characters per value. Reads resolve labels for
 * the member's language (`metadata.model(ctx)`), so every entity below carries plain strings;
 * editors read the written values with `?raw=1` (the `Raw*` schemas).
 */
export const LabelText = localizedText({ minLength: 1, maxLength: 80 });
/** A translatable description (up to 2000 characters per locale). */
export const DescriptionText = localizedText({ maxLength: 2000 });
/**
 * A label typed in one language — the writer's (stage editor rows, AI proposals). Written onto a
 * localized label it sets only that language (see `MetadataService`).
 */
export const PlainLabel = z.string().min(1).max(80);

// ---------------------------------------------------------------------------------------------
// Apps
// ---------------------------------------------------------------------------------------------

/** Where a metadata element came from (blueprint install, connector, customer). */
export const ElementSource = z.object({
  kind: z.enum(['blueprint', 'custom', 'builtin', 'connector']),
  /** Blueprint key (`dataroom.sales-crm`) or connection id, when applicable. */
  key: z.string().nullable().default(null),
  version: z.string().nullable().default(null),
  /** Human label shown in the UI, e.g. "Sales CRM 1.4", "Microsoft 365", "Custom · Mara K." */
  label: z.string().nullable().default(null),
});
export type ElementSource = z.infer<typeof ElementSource>;

export const App = z.object({
  id: Uuid,
  key: ApiKey,
  label: z.string(),
  icon: z.string().nullable(),
  position: z.number().int(),
  stableKey: z.string().nullable(),
  source: ElementSource,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type App = z.infer<typeof App>;

export const CreateAppInput = z.object({
  key: ApiKey,
  label: LabelText,
  icon: z.string().max(40).nullish(),
  position: z.number().int().optional(),
  stableKey: StableKey.nullish(),
  source: ElementSource.partial().optional(),
});
export type CreateAppInput = z.infer<typeof CreateAppInput>;

export const UpdateAppInput = CreateAppInput.omit({ key: true }).partial();
export type UpdateAppInput = z.infer<typeof UpdateAppInput>;

// ---------------------------------------------------------------------------------------------
// Object types
// ---------------------------------------------------------------------------------------------

/** Well-known traits. Traits are open strings; these are the ones the engine understands. */
export const KNOWN_TRAITS = ['organization', 'person'] as const;
export const Trait = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[a-z][a-z0-9_]*$/);

export const StorageTier = z.enum(['shared', 'dedicated']);
export type StorageTier = z.infer<typeof StorageTier>;

export const ObjectType = z.object({
  id: Uuid,
  appId: Uuid.nullable(),
  key: ApiKey,
  labelSingular: z.string(),
  labelPlural: z.string(),
  description: z.string().nullable(),
  icon: ObjectIcon,
  color: ObjectColor,
  traits: z.array(z.string()),
  behavior: ObjectBehavior.optional(),
  source: ElementSource,
  storageTier: StorageTier,
  showInNav: z.boolean(),
  titleFieldId: Uuid.nullable(),
  position: z.number().int(),
  stableKey: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ObjectType = z.infer<typeof ObjectType>;

export const CreateObjectTypeInput = z.object({
  key: ApiKey,
  appId: Uuid.nullish(),
  labelSingular: LabelText,
  labelPlural: LabelText,
  description: DescriptionText.nullish(),
  icon: ObjectIcon.default('box'),
  color: ObjectColor.default('slate'),
  traits: z.array(Trait).max(20).default([]),
  behavior: ObjectBehavior.optional(),
  showInNav: z.boolean().default(true),
  position: z.number().int().optional(),
  stableKey: StableKey.nullish(),
  source: ElementSource.partial().optional(),
  /** Label of the title field created with the object (default "Name"). */
  titleFieldLabel: LabelText.default('Name'),
});
export type CreateObjectTypeInput = z.input<typeof CreateObjectTypeInput>;

export const UpdateObjectTypeInput = z
  .object({
    appId: Uuid.nullable(),
    labelSingular: LabelText,
    labelPlural: LabelText,
    description: DescriptionText.nullable(),
    icon: ObjectIcon,
    color: ObjectColor,
    traits: z.array(Trait).max(20),
    behavior: PartialObjectBehavior,
    showInNav: z.boolean(),
    position: z.number().int(),
    titleFieldId: Uuid,
    /** Stable field key, including a field created earlier in the same atomic change package. */
    titleFieldKey: ApiKey,
  })
  .partial()
  .refine((input) => !(input.titleFieldId && input.titleFieldKey), {
    path: ['titleFieldKey'],
    message: 'Choose titleFieldId or titleFieldKey, not both',
  });
export type UpdateObjectTypeInput = z.infer<typeof UpdateObjectTypeInput>;

/** Object type with cheap statistics for list screens. `records` is an estimate. */
export const ObjectTypeSummary = ObjectType.extend({
  recordCount: z.number().int(),
  recordCountEstimated: z.boolean(),
  fieldCount: z.number().int(),
  relationCount: z.number().int(),
});
export type ObjectTypeSummary = z.infer<typeof ObjectTypeSummary>;

// ---------------------------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------------------------

/** Semantic role of a field; lets generic screens find "the amount", "the owner", … */
export const FIELD_ROLES = [
  'title',
  'owner',
  'amount',
  'close_date',
  'stage',
  'stage_changed_at',
  'pipeline',
  'probability',
  'domain',
  'email',
  'phone',
  'location',
  'score',
  'status',
  'start',
  'end',
  'due',
  'done',
  'type',
  'description',
  'progress',
] as const;
export const FieldRole = z.enum(FIELD_ROLES);
export type FieldRole = z.infer<typeof FieldRole>;

export const FieldTypeKey = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[a-z][a-z0-9_]*$/);

export const Field = z.object({
  id: Uuid,
  /** Stable id used as storage key (`records.data`), never changes: `fld_…`. */
  stableId: z.string(),
  objectTypeId: Uuid,
  key: ApiKey,
  label: z.string(),
  description: z.string().nullable(),
  type: FieldTypeKey,
  config: z.record(z.string(), z.unknown()),
  role: FieldRole.nullable(),
  required: z.boolean(),
  unique: z.boolean(),
  indexed: z.boolean(),
  readOnly: z.boolean(),
  isSystem: z.boolean(),
  /** Layout grouping hint (e.g. "Company", "Commercial"). */
  group: z.string().nullable(),
  position: z.number().int(),
  stableKey: z.string().nullable(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Field = z.infer<typeof Field>;

export const CreateFieldInput = z.object({
  key: ApiKey,
  label: LabelText,
  description: DescriptionText.nullish(),
  type: FieldTypeKey,
  config: z.record(z.string(), z.unknown()).default({}),
  role: FieldRole.nullish(),
  required: z.boolean().default(false),
  unique: z.boolean().default(false),
  indexed: z.boolean().default(false),
  readOnly: z.boolean().default(false),
  group: z.string().max(80).nullish(),
  position: z.number().int().optional(),
  stableKey: StableKey.nullish(),
});
export type CreateFieldInput = z.input<typeof CreateFieldInput>;

export const UpdateFieldInput = z
  .object({
    label: LabelText,
    key: ApiKey,
    description: DescriptionText.nullable(),
    config: z.record(z.string(), z.unknown()),
    role: FieldRole.nullable(),
    required: z.boolean(),
    unique: z.boolean(),
    indexed: z.boolean(),
    readOnly: z.boolean(),
    group: z.string().max(80).nullable(),
    position: z.number().int(),
  })
  .partial();
export type UpdateFieldInput = z.infer<typeof UpdateFieldInput>;

export const ReorderInput = z.object({ ids: z.array(Uuid).min(1).max(2000) });
export type ReorderInput = z.infer<typeof ReorderInput>;

// ---------------------------------------------------------------------------------------------
// Relations
// ---------------------------------------------------------------------------------------------

/** Cardinality seen from the `from` object: deals → company is `many_to_one`. */
export const Cardinality = z.enum(['one_to_one', 'one_to_many', 'many_to_one', 'many_to_many']);
export type Cardinality = z.infer<typeof Cardinality>;

export const OnDelete = z.enum(['unlink', 'cascade', 'restrict']);
export type OnDelete = z.infer<typeof OnDelete>;

/**
 * How a link field shows in related grids and linked cards (plan §7.3, data-model-objects; rendered
 * by record-related): as a chip or as plain text, in the option's colour or neutral, with an optional
 * column width. Generic code never branches on link-field keys: it reads these hints.
 */
export const LinkFieldDisplay = z
  .object({
    as: z.enum(['chip', 'text']).default('text'),
    tone: z.enum(['option', 'neutral']).default('neutral'),
    /** Column width in related grids (px). */
    width: z.number().int().min(48).max(640).optional(),
  })
  .strict();
export type LinkFieldDisplay = z.infer<typeof LinkFieldDisplay>;

/**
 * A link field whose value is computed from the other link fields of the same link
 * (`quantity * unit_price`). The expression reads link fields by key, is checked on every write
 * (unknown names and type errors are rejected) and its result is stored with the link, so every
 * reader (related grids, Insight, document bindings) sees it like any other link value.
 */
export const LinkFieldComputed = z.object({ expr: z.string().trim().min(1).max(2000) }).strict();
export type LinkFieldComputed = z.infer<typeof LinkFieldComputed>;

export const LinkField = z.object({
  id: Uuid,
  relationId: Uuid,
  stableId: z.string(),
  key: ApiKey,
  label: z.string(),
  type: FieldTypeKey,
  config: z.record(z.string(), z.unknown()),
  position: z.number().int(),
  /** Display hints for related grids and linked cards; absent = plain text. */
  display: LinkFieldDisplay.optional(),
  /** Computed from the other link fields of the link; absent = typed in. */
  computed: LinkFieldComputed.optional(),
});
export type LinkField = z.infer<typeof LinkField>;

export const Relation = z.object({
  id: Uuid,
  key: ApiKey,
  fromObjectTypeId: Uuid,
  toObjectTypeId: Uuid,
  /** Relation field on the `from` object (e.g. deals.company). */
  fromFieldId: Uuid,
  /** Inverse relation field on the `to` object (e.g. companies.deals). */
  toFieldId: Uuid,
  cardinality: Cardinality,
  onDelete: OnDelete,
  linkFields: z.array(LinkField),
  stableKey: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Relation = z.infer<typeof Relation>;

export const CreateRelationInput = z.object({
  key: ApiKey,
  fromObject: ApiKey,
  toObject: ApiKey,
  cardinality: Cardinality,
  onDelete: OnDelete.default('unlink'),
  fromField: z.object({ key: ApiKey, label: LabelText, stableKey: StableKey.nullish() }),
  toField: z.object({ key: ApiKey, label: LabelText, stableKey: StableKey.nullish() }),
  stableKey: StableKey.nullish(),
});
export type CreateRelationInput = z.input<typeof CreateRelationInput>;

export const UpdateRelationInput = z.object({ cardinality: Cardinality, onDelete: OnDelete }).partial();
export type UpdateRelationInput = z.infer<typeof UpdateRelationInput>;

export const CreateLinkFieldInput = z.object({
  key: ApiKey,
  label: LabelText,
  type: FieldTypeKey,
  config: z.record(z.string(), z.unknown()).default({}),
  position: z.number().int().optional(),
  display: LinkFieldDisplay.nullish(),
  computed: LinkFieldComputed.nullish(),
});
export type CreateLinkFieldInput = z.input<typeof CreateLinkFieldInput>;

export const UpdateLinkFieldInput = z
  .object({
    label: LabelText,
    config: z.record(z.string(), z.unknown()),
    position: z.number().int(),
    /** null removes the hints. */
    display: LinkFieldDisplay.nullable(),
    /** null makes the link field typed in again (stored values are kept). */
    computed: LinkFieldComputed.nullable(),
  })
  .partial();
export type UpdateLinkFieldInput = z.infer<typeof UpdateLinkFieldInput>;

// ---------------------------------------------------------------------------------------------
// Pipelines & stages
// ---------------------------------------------------------------------------------------------

/**
 * Where a stage stands in its pipeline: `open` (still in progress), `won` (closed successfully) or
 * `lost` (closed unsuccessfully). The stored values keep their historical names; screens present
 * them generically ("Open", "Successful", "Unsuccessful") since a ticket or hiring pipeline has
 * nothing to win.
 */
export const StageGroup = z.enum(['open', 'won', 'lost']);
export type StageGroup = z.infer<typeof StageGroup>;

/** A stage probability in percent; null = the pipeline does not weight records by probability. */
const StageProbability = z.number().int().min(0).max(100);

/**
 * Entry condition of a stage: `expr` (validation context of the object, must return True/false)
 * and the message shown when a record may not enter (the writer's language; null = a generic one).
 */
export const StageEntryCondition = z
  .object({
    expr: z.string().trim().min(1).max(2000),
    message: z.string().trim().max(300).nullable().default(null),
  })
  .strict();
export type StageEntryCondition = z.infer<typeof StageEntryCondition>;

export const Stage = z.object({
  id: Uuid,
  pipelineId: Uuid,
  key: ApiKey,
  label: z.string(),
  color: HexColor,
  group: StageGroup,
  /**
   * Likelihood (percent) that a record in this open stage closes successfully; it weights forecasts
   * and fills a `probability` field. Null = not used (most non-sales pipelines). See `stageProbability`.
   */
  probability: StageProbability.nullable(),
  /** Days after which a record in this open stage counts as stale; null = never. */
  staleAfterDays: z.number().int().positive().nullable(),
  /** Field api keys that must be filled before a record may enter this stage. */
  requiredFields: z.array(z.string()),
  /**
   * A True/false expression over the record's fields that must hold for a record to enter this
   * stage (`amount > 0`), checked like a validation rule; null = no condition.
   */
  entryCondition: StageEntryCondition.nullable().optional(),
  onEnterWorkflowId: Uuid.nullable(),
  position: z.number().int(),
  stableKey: z.string().nullable(),
});
export type Stage = z.infer<typeof Stage>;

type StageProbabilityInput = { group: StageGroup; probability?: number | null | undefined };

/**
 * The probability (percent) a stage gives its records, for weighted values and the `probability`
 * field: closed successfully counts 100, closed unsuccessfully 0, an open stage its own probability.
 * Null = the open stage has none, so its records are left out of weighted values.
 */
export function stageProbability(stage: StageProbabilityInput): number | null {
  if (stage.group === 'won') return 100;
  if (stage.group === 'lost') return 0;
  return stage.probability ?? null;
}

/** True when a pipeline weights records by probability: one of its open stages has one. */
export function usesStageProbability(stages: readonly StageProbabilityInput[]): boolean {
  return stages.some((s) => s.group === 'open' && s.probability != null);
}

export const Pipeline = z.object({
  id: Uuid,
  objectTypeId: Uuid,
  /** The status field whose values are this pipeline's stage keys. */
  fieldId: Uuid,
  key: ApiKey,
  label: z.string(),
  isDefault: z.boolean(),
  position: z.number().int(),
  stableKey: z.string().nullable(),
  stages: z.array(Stage),
});
export type Pipeline = z.infer<typeof Pipeline>;

export const CreateStageInput = z.object({
  key: ApiKey,
  label: LabelText,
  color: HexColor.default('#98A2B3'),
  group: StageGroup.default('open'),
  /** Absent = the stage has no probability. */
  probability: StageProbability.nullish(),
  staleAfterDays: z.number().int().positive().nullish(),
  requiredFields: z.array(z.string()).default([]),
  entryCondition: StageEntryCondition.nullish(),
  onEnterWorkflowId: Uuid.nullish(),
  position: z.number().int().optional(),
  stableKey: StableKey.nullish(),
});
export type CreateStageInput = z.input<typeof CreateStageInput>;

export const UpdateStageInput = z
  .object({
    key: ApiKey,
    label: LabelText,
    color: HexColor,
    group: StageGroup,
    probability: StageProbability.nullable(),
    staleAfterDays: z.number().int().positive().nullable(),
    requiredFields: z.array(z.string()),
    entryCondition: StageEntryCondition.nullable(),
    onEnterWorkflowId: Uuid.nullable(),
    position: z.number().int(),
  })
  .partial();
export type UpdateStageInput = z.infer<typeof UpdateStageInput>;

export const CreatePipelineInput = z.object({
  object: ApiKey,
  /** Api key of the status field (e.g. `stage`). */
  field: ApiKey,
  key: ApiKey,
  label: LabelText,
  isDefault: z.boolean().default(false),
  stableKey: StableKey.nullish(),
  stages: z.array(CreateStageInput).max(50).default([]),
});
export type CreatePipelineInput = z.input<typeof CreatePipelineInput>;

export const UpdatePipelineInput = z
  .object({ label: LabelText, isDefault: z.boolean(), position: z.number().int() })
  .partial();
export type UpdatePipelineInput = z.infer<typeof UpdatePipelineInput>;

// ---------------------------------------------------------------------------------------------
// Whole workspace model (one request for the web shell)
// ---------------------------------------------------------------------------------------------

export const WorkspaceModel = z.object({
  apps: z.array(App),
  objects: z.array(ObjectType),
  fields: z.array(Field),
  relations: z.array(Relation),
  pipelines: z.array(Pipeline),
});
export type WorkspaceModel = z.infer<typeof WorkspaceModel>;

// ---------------------------------------------------------------------------------------------
// Field type listing (GET /field-types)
// ---------------------------------------------------------------------------------------------

export const FIELD_TYPE_CATEGORIES = [
  'text',
  'number',
  'date',
  'choice',
  'contact',
  'relation',
  'computed',
  'file',
  'location',
  'advanced',
] as const;
export const FieldTypeCategory = z.enum(FIELD_TYPE_CATEGORIES);
export type FieldTypeCategory = z.infer<typeof FieldTypeCategory>;

export const OperatorInfo = z.object({
  key: z.string(),
  label: z.string(),
  /** 'none' (is_empty), 'single', 'multi' (in), 'range' (between), 'relative' ($rel) */
  arity: z.enum(['none', 'single', 'multi', 'range', 'relative']),
});
export type OperatorInfo = z.infer<typeof OperatorInfo>;

export const FieldTypeInfo = z.object({
  key: z.string(),
  label: z.string(),
  glyph: z.string(),
  category: FieldTypeCategory,
  description: z.string(),
  operators: z.array(OperatorInfo),
  aggregations: z.array(z.string()),
  sortable: z.boolean(),
  groupable: z.boolean().optional(),
  /** Where the type can be a field: objects, task types (default: both). */
  hosts: z.array(z.enum(['object', 'task'])).optional(),
  /** Fields of this type may hold several values (`config.multiple`). */
  multiple: z.boolean().optional(),
  /** JSON Schema of the config, for generic config editors. */
  configJsonSchema: z.unknown(),
});
export type FieldTypeInfo = z.infer<typeof FieldTypeInfo>;

/** GET /objects/:key — object with its fields, relations and pipelines. */
export const ObjectDetail = ObjectTypeSummary.extend({
  fields: z.array(Field),
  relations: z.array(Relation),
  pipelines: z.array(Pipeline),
});
export type ObjectDetail = z.infer<typeof ObjectDetail>;

// ---------------------------------------------------------------------------------------------
// Raw (as written) metadata and resolution for a language (plan §8.10)
// ---------------------------------------------------------------------------------------------

/** Raw app: `label` as written (plain string or one value per locale). */
export const RawApp = App.extend({ label: LocalizedText });
export type RawApp = z.infer<typeof RawApp>;

export const RawObjectType = ObjectType.extend({
  labelSingular: LocalizedText,
  labelPlural: LocalizedText,
  description: LocalizedText.nullable(),
});
export type RawObjectType = z.infer<typeof RawObjectType>;

/** Raw field: `label`, `description` and the option labels in `config.options[].label` as written. */
export const RawField = Field.extend({ label: LocalizedText, description: LocalizedText.nullable() });
export type RawField = z.infer<typeof RawField>;

export const RawLinkField = LinkField.extend({ label: LocalizedText });
export type RawLinkField = z.infer<typeof RawLinkField>;

export const RawRelation = Relation.extend({ linkFields: z.array(RawLinkField) });
export type RawRelation = z.infer<typeof RawRelation>;

export const RawStage = Stage.extend({ label: LocalizedText });
export type RawStage = z.infer<typeof RawStage>;

export const RawPipeline = Pipeline.extend({ label: LocalizedText, stages: z.array(RawStage) });
export type RawPipeline = z.infer<typeof RawPipeline>;

/** GET /model?raw=1 — the workspace model with every translatable text as written. */
export const RawWorkspaceModel = z.object({
  apps: z.array(RawApp),
  objects: z.array(RawObjectType),
  fields: z.array(RawField),
  relations: z.array(RawRelation),
  pipelines: z.array(RawPipeline),
});
export type RawWorkspaceModel = z.infer<typeof RawWorkspaceModel>;

/** GET /objects/:key?raw=1 */
export const RawObjectDetail = RawObjectType.extend({
  recordCount: z.number().int(),
  recordCountEstimated: z.boolean(),
  fieldCount: z.number().int(),
  relationCount: z.number().int(),
  fields: z.array(RawField),
  relations: z.array(RawRelation),
  pipelines: z.array(RawPipeline),
});
export type RawObjectDetail = z.infer<typeof RawObjectDetail>;

/** Translatable properties per element kind (`option` = a choice label in `config.options`). */
export const TRANSLATABLE_PROPS = {
  app: ['label'],
  object: ['labelSingular', 'labelPlural', 'description'],
  field: ['label', 'description', 'unit'],
  linkField: ['label', 'unit'],
  pipeline: ['label'],
  stage: ['label'],
  option: ['label'],
  // Named workspace elements (shared ones; private views and dashboards are personal).
  view: ['name'],
  layout: ['name'],
  form: ['name'],
  dashboard: ['name', 'description'],
  workflow: ['name', 'description'],
  taskType: ['name', 'description'],
  taskTypeField: ['label'],
} as const;
export const TranslatableKind = z.enum([
  'app',
  'object',
  'field',
  'linkField',
  'pipeline',
  'stage',
  'option',
  'view',
  'layout',
  'form',
  'dashboard',
  'workflow',
  'taskType',
  'taskTypeField',
]);
export type TranslatableKind = z.infer<typeof TranslatableKind>;
export const TranslatableProp = z.enum([
  'label',
  'labelSingular',
  'labelPlural',
  'description',
  'name',
  /** The unit of a number, formula, lookup or rollup field ("months"): `config.unit`. */
  'unit',
]);
export type TranslatableProp = z.infer<typeof TranslatableProp>;

type OptionLike = { key?: unknown; label?: unknown };

/** The option list of a choice field config (`config.options`), or null. */
export function optionsOf(config: Readonly<Record<string, unknown>>): OptionLike[] | null {
  const options = config.options;
  return Array.isArray(options) ? (options as OptionLike[]) : null;
}

/**
 * A field config with every option label and the unit resolved for `locale` (the same object when
 * none is localized).
 */
export function resolveFieldConfig(config: Record<string, unknown>, locale: string): Record<string, unknown> {
  const options = optionsOf(config);
  const unitText = localizedTextOf(config.unit);
  const unit = isLocalizedTextMap(unitText);
  const localizedOptions = options?.some((o) => isLocalizedTextMap(localizedTextOf(o.label))) ?? false;
  if (!localizedOptions && !unit) return config;
  return {
    ...config,
    ...(unit && unitText ? { unit: resolveText(unitText, locale) } : {}),
    ...(localizedOptions && options
      ? {
          options: options.map((o) =>
            isLocalizedTextMap(localizedTextOf(o.label))
              ? { ...o, label: resolveText(localizedTextOf(o.label) ?? '', locale) }
              : o,
          ),
        }
      : {}),
  };
}

const textOrNull = (t: LocalizedText | null, locale: string) => (t == null ? null : resolveText(t, locale));

export const resolveApp = (a: RawApp, locale: string): App => ({ ...a, label: resolveText(a.label, locale) });

export const resolveObjectType = (o: RawObjectType, locale: string): ObjectType => ({
  ...o,
  labelSingular: resolveText(o.labelSingular, locale),
  labelPlural: resolveText(o.labelPlural, locale),
  description: textOrNull(o.description, locale),
});

export const resolveField = (f: RawField, locale: string): Field => ({
  ...f,
  label: resolveText(f.label, locale),
  description: textOrNull(f.description, locale),
  config: resolveFieldConfig(f.config, locale),
});

export const resolveLinkField = (l: RawLinkField, locale: string): LinkField => ({
  ...l,
  label: resolveText(l.label, locale),
  config: resolveFieldConfig(l.config, locale),
});

export const resolveRelation = (r: RawRelation, locale: string): Relation => ({
  ...r,
  linkFields: r.linkFields.map((l) => resolveLinkField(l, locale)),
});

export const resolveStage = (s: RawStage, locale: string): Stage => ({
  ...s,
  label: resolveText(s.label, locale),
});

export const resolvePipeline = (p: RawPipeline, locale: string): Pipeline => ({
  ...p,
  label: resolveText(p.label, locale),
  stages: p.stages.map((s) => resolveStage(s, locale)),
});

/**
 * The workspace model in one language: every label, description and option label resolved with
 * `resolveText` (exact locale → language → English → first value). Plain strings pass through, so
 * a model without translations resolves to an equal model.
 */
export function resolveWorkspaceModel(raw: RawWorkspaceModel, locale: string): WorkspaceModel {
  return {
    apps: raw.apps.map((a) => resolveApp(a, locale)),
    objects: raw.objects.map((o) => resolveObjectType(o, locale)),
    fields: raw.fields.map((f) => resolveField(f, locale)),
    relations: raw.relations.map((r) => resolveRelation(r, locale)),
    pipelines: raw.pipelines.map((p) => resolvePipeline(p, locale)),
  };
}

// ---------------------------------------------------------------------------------------------
// Translations (GET/PUT /metadata/translations?locale=de)
// ---------------------------------------------------------------------------------------------

/**
 * One translatable text of the workspace. `optionKey` addresses an option of a choice field,
 * `fieldKey` a field of a task type.
 */
export const TranslationRef = z
  .object({
    kind: TranslatableKind,
    /** Element id (for `option`: the field id; for `taskType` and `taskTypeField`: the type key). */
    id: z.string().min(1).max(80),
    prop: TranslatableProp,
    optionKey: z.string().min(1).max(80).optional(),
    fieldKey: z.string().min(1).max(63).optional(),
  })
  .refine((r) => (r.kind === 'option') === (r.optionKey !== undefined), {
    message: 'optionKey is required for options and only for options',
    path: ['optionKey'],
  })
  .refine((r) => (r.kind === 'taskTypeField') === (r.fieldKey !== undefined), {
    message: 'fieldKey is required for task type fields and only for them',
    path: ['fieldKey'],
  })
  .refine((r) => ['taskType', 'taskTypeField'].includes(r.kind) || Uuid.safeParse(r.id).success, {
    message: 'Invalid element id',
    path: ['id'],
  })
  .refine((r) => (TRANSLATABLE_PROPS[r.kind] as readonly string[]).includes(r.prop), {
    message: 'This element has no such translatable property',
    path: ['prop'],
  });
export type TranslationRef = z.infer<typeof TranslationRef>;

export const TranslationEntry = z.object({
  ref: TranslationRef,
  /** Readable address: `deals.labelPlural`, `deals.stage.label`, `deals.priority.options.high`. */
  path: z.string(),
  /** Text in the source language (what translators translate from). */
  source: z.string(),
  /** Locale the source text is written in. */
  sourceLocale: z.string(),
  /** Text in the requested locale; null when it is missing. */
  value: z.string().nullable(),
  missing: z.boolean(),
});
export type TranslationEntry = z.infer<typeof TranslationEntry>;

export const ObjectTranslations = z.object({
  id: Uuid,
  key: z.string(),
  /** Plural label in the requested locale (the source text while it is missing). */
  label: z.string(),
  total: z.number().int(),
  missing: z.number().int(),
  entries: z.array(TranslationEntry),
});
export type ObjectTranslations = z.infer<typeof ObjectTranslations>;

export const TranslationsReport = z.object({
  locale: z.string(),
  /** Language untagged (plain-string) labels of custom elements are written in: the workspace's. */
  workspaceLocale: z.string(),
  total: z.number().int(),
  missing: z.number().int(),
  /** App labels (navigation groups). */
  apps: z.object({
    total: z.number().int(),
    missing: z.number().int(),
    entries: z.array(TranslationEntry),
  }),
  /**
   * Per object: object, field, option, pipeline, stage and link field labels, then the names of the
   * object's shared views, its record layouts and forms (`deals.views.pipeline`).
   */
  objects: z.array(ObjectTranslations),
  /**
   * Workspace-wide named elements: shared dashboards, workflows and task types with their field
   * labels (`dashboards.pipeline`, `workflows.deal-won.description`, `taskTypes.call.fields.person`).
   */
  workspace: z.object({
    total: z.number().int(),
    missing: z.number().int(),
    entries: z.array(TranslationEntry),
  }),
});
export type TranslationsReport = z.infer<typeof TranslationsReport>;

export const PutTranslationsInput = z.object({
  /** Locale of the texts (`de`, `de-AT`, `fr`, …). */
  locale: LocaleCode,
  /** New texts of that locale; `null` or `''` removes the translation. */
  entries: z
    .array(z.object({ ref: TranslationRef, value: z.string().max(2000).nullable() }))
    .min(1)
    .max(2000),
});
export type PutTranslationsInput = z.infer<typeof PutTranslationsInput>;
